import { useAuthStore } from '@/stores/auth.store';

import axios, { AxiosHeaders } from 'axios';
import type { AxiosInstance } from 'axios';
import tokenService from '../services/token.service';
import cookieService from '../services/cookie.service';
import i18n from './i18n';

// Axios instance for tenant-agnostic auth endpoints
let authAxiosInstance: AxiosInstance | null = null;

/**
 * Get an axios instance configured for tenant-agnostic auth endpoints.
 * Uses the base API URL without tenant prefix.
 */
export const getAuthAxios = (): AxiosInstance => {
  if (!authAxiosInstance) {
    const apiUrl = import.meta.env.VITE_INOCU_API_URL;

    authAxiosInstance = axios.create({
      baseURL: apiUrl,
    });

    // Add request interceptor for auth headers
    authAxiosInstance.interceptors.request.use(config => {
      // @ts-ignore
      const lang = (i18n.global.locale as any).value;
      (config.headers as AxiosHeaders).set('Accept-Language', lang);

      const accessToken = tokenService.getAccessToken();
      if (accessToken) {
        (config.headers as AxiosHeaders).set('authorization', `Bearer ${accessToken}`);
      }

      return config;
    });
  }

  return authAxiosInstance;
};

export const configureApi = () => {
  const tenant = cookieService.get('tenant');
  const apiUrl = import.meta.env.VITE_INOCU_API_URL;
  const baseUrl = tenant ? `${apiUrl}/${tenant}` : apiUrl;

  axios.defaults.baseURL = baseUrl;

  // Add JWT to each request
  axios.interceptors.request.use(config => {
    // Add language
    // @ts-ignore
    const lang = (i18n.global.locale as any).value;
    (config.headers as AxiosHeaders).set('Accept-Language', lang);

    // Add access token
    const accessToken = tokenService.getAccessToken();
    const currentCustomerId = tokenService.getCurrentCustomerId();
    if (accessToken) (config.headers as AxiosHeaders).set('authorization', `Bearer ${accessToken}`);

    if (currentCustomerId)
      (config.headers as AxiosHeaders).set('X-INOCU-CUSTOMER-ID', currentCustomerId);

    return config;
  });

  // Logout automatically if Unauthorized, ie. token expired
  axios.interceptors.response.use(
    response => response,
    async error => {
      const originalConfig = error.config;

      if (originalConfig.url !== '/users/init_auth/' && error.response) {
        // Access Token was expired
        if (error.response.status === 401 && !originalConfig._retry) {
          originalConfig._retry = true;

          try {
            const { data } = await getAuthAxios().post('/auth/refresh_token/', {
              access_token: tokenService.getAccessToken(),
              refresh_token: tokenService.getRefreshToken(),
            });

            const { access_token } = data;

            tokenService.setAccessToken(access_token);

            return axios(originalConfig);
          } catch (_error) {
            const { logout } = useAuthStore();

            logout();

            return Promise.reject(_error);
          }
        }
      }

      return Promise.reject(error);
    }
  );
};
